import {useEffect, useState, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'

import {connect} from 'react-redux'

import {get}from 'lodash'

import {index} from '../../../apis'

import {useIsMounted} from '../../../hooks'

import {
  makeGetPerformanceById,
  selectPerformanceLinks,
  makeGetPerformancePerformers,
  selectPerformanceFetchedAssociations,
} from '../../../selectors'

import {
  saveLinks,
  setFetchedAssociations,
  addPerformersToPerformance,
} from '../../../actions'

import Dialog from '@mui/material/Dialog'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import IconButton from '@mui/material/IconButton'
import Avatar from '@mui/material/Avatar'
import AvatarGroup from '@mui/material/AvatarGroup'

import AddIcon from '@mui/icons-material/Add'
import LockIcon from '@mui/icons-material/Lock'
import RemoveIcon from '@mui/icons-material/Remove'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import BandRow from '../../cards/BandRow'
import ArtistRow from '../../cards/ArtistRow'
import InvitePerformers from '../performers/InvitePerformers'
import RemovePerformers from '../performers/RemovePerformers'
import PerformancePermissions from '../permissions/PerformancePermissions'

import SlideTransition from '../../generics/SlideTransition'
import FadeTransition from '../../generics/FadeTransition'



const cssStyles = ({isLargeScreen}) => ({
  root: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 0px;
    border-top: 1px solid #333;
    border-bottom: 1px solid #333;
    padding: 15px 0 25px 0;
  `,
  lineupSection: css`
    width: 100%;
    padding: ${isLargeScreen ? '0px' : '0 25px'};
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    h4 {
      font-size: 1.2rem;
      font-weight: 500;
      margin: 0;
    }
  `,
  lineupHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #fff;
    margin-bottom: 10px;
  `,
  lineupButtons: css`
    display: flex;
    gap: 5px;
    align-items: center;
  `,
  lineupContainer: css`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 25px;
  `,
  truncatedText: css`
    display: -webkit-box;
    -webkit-line-clamp: 10;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `,
  expandedLineup: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 7px;
  `,
  lineupFooter: css`
    display: flex;
    justify-content: center;
    width: 100%;
    position: relative;
    min-height: 36px;
    margin-top: 15px;
  `,
  expandMoreContainer: css`
    position: absolute;
    right: 0;
    width: 36px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  `,
  showMoreContainer: css`
    display: flex;
    justify-content: center;
  `,
  showMoreButton: css`
    height: 36px;
    width: 110px;
    border-radius: 18px;
    background: #000;
    color: #3ea5ff;
    font-weight: 500;
    border: none;
    text-transform: uppercase;
    font-size: 11px;
  `,
  dialog: css`
    backdrop-filter: blur(10px);
  `,
})




const EventLineup = (props) => {
  const isMounted = useIsMounted()

  const isLargeScreen = useMediaQuery('(min-width:1120px)')
  const isExtraSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const styles = cssStyles({isLargeScreen})

  const {
    isLoaded,
    saveLinks,
    performers,
    permission,
    stateLinks,
    performanceId,
    setFetchedAssociations,
    addPerformersToPerformance
  } = props


  // If there are 3 or fewer performers, always show the expanded view.
  const alwaysExpanded = performers.length > 0 && performers.length <= 3


  const [isFetched, setIsFetched] = useState(isLoaded)
  const [isFetching, setIsFetching] = useState(false)
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded)

  const [modal, setModal] = useState(false)

  const [links, setLinks] = useState(stateLinks)
  const hasNextPage = links.pages !== links.page



  const fetchMorePerformers = useCallback(() => {
    if (hasNextPage) {
      const url = links.next_url.slice(4)

      index(url, 'PERFORMANCES').then((response) => {
        addPerformersToPerformance(performanceId, response.data)

        if (isMounted.current) {
          setIsFetching(false)
          if (response.links) {
            setLinks(response.links)
          }
          setIsFetched(true)
        }
      })
    }
  }, [
    isMounted,
    hasNextPage,
    performanceId,
    links.next_url, 
    addPerformersToPerformance
  ])



  useEffect(() => {
    return () => saveLinks('PERFORMANCE', performanceId, 'performers', links)
  }, [performanceId, links, saveLinks])



  useEffect(() => {
    if (!isLoaded) {
      setIsFetching(true)
      setFetchedAssociations('PERFORMANCE', performanceId, 'performers')

      index(`performances/${performanceId}/performers`).then((response) => {
        addPerformersToPerformance(performanceId, response.data)

        if (isMounted.current) {
          if (response.data.links) {
            setLinks(response.data.links)
          }
          setIsFetched(true)
          setIsFetching(false)
        }
      })
      .catch(() => {
        if (isMounted.current) {
          setIsFetched(true)
          setIsFetching(false)
        }
      })
    }
  }, [
    isLoaded,
    isMounted,
    performanceId,
    setFetchedAssociations,
    addPerformersToPerformance
  ])



  const open = (content) => () => setModal(content)

  const close = () => setModal('')

  const toggleExpand = () => {
    if (!alwaysExpanded) {
      setIsExpanded((prev) => !prev)
    }
  }


  return ((isFetched && performers.length === 0 && !['owner', 'editor'].includes(permission)) 
    ? null
    : <div css={styles.root}>
        <section css={styles.lineupSection}>
          <div css={styles.lineupHeader}>
            {isFetched
              ? <h4>Lineup</h4>
              : <Skeleton variant='h4' width='70px' />
            }


            {['owner', 'editor'].includes(permission) && (
              <div>
                <IconButton fontSize='small' onClick={open('invite')}>
                  <AddIcon color='secondary' fontSize='small' />
                </IconButton>

                <IconButton fontSize='small' onClick={open('remove')}>
                  <RemoveIcon color='secondary' fontSize='small' />
                </IconButton>

                {permission === 'owner' && (
                  <IconButton fontSize='small' onClick={open('permissions')}>
                    <LockIcon color='secondary' fontSize='small' />
                  </IconButton>
                )}
              </div>
            )}
          </div>


          <div
            css={styles.lineupContainer}
            {...(!alwaysExpanded && {onClick: toggleExpand})}
          >

            {!isFetched &&
              <Skeleton variant='rectangular' width='100%' height={70} />
            }


            {isFetched &&
              <>
                {(alwaysExpanded || isExpanded) ? (
                  <div css={styles.expandedLineup}>
                    {performers.map((performer) => {
                      if (performer.type === 'artist') {
                        return <ArtistRow key={performer.id} artist={performer} />
                      } else {
                        return <BandRow key={performer.id} band={performer} />
                      }
                    })}
                  </div>
                ) : (
                  <>
                    <AvatarGroup 
                      max={4} 
                      spacing='small'
                      sx={{
                        '& .MuiAvatarGroup-avatar': {
                          fontSize: '0.9rem',
                          border: '1px solid #000 !important',
                          background: '#333'
                        },
                      }}
                    >
                      {performers.map((performer) => {
                        const {attributes} = performer
                        const imageURL = attributes.imageDerivatives.small || attributes.externalImageUrl

                        return (
                          <Avatar
                            key={performer.id}
                            alt={attributes.name}
                            src={imageURL}
                            sx={{
                              border: '1px solid #000 !important',
                              '& .MuiAvatar-fallback': {
                                color: 'lightgray !important',
                                width: '20px'
                              }
                            }}
                          />
                        )
                      })}
                    </AvatarGroup>

                    <div>
                      <p css={styles.truncatedText}>
                        {performers.map((p) => p.attributes.name).join(', ')}
                      </p>
                    </div>
                  </>
                )}
              </>
            }
          </div>



          {/* Render the footer only if there is an expand option or a "Show More" option */}
          {((!alwaysExpanded && performers.length > 0) || hasNextPage) && (
            <div css={styles.lineupFooter}>
              {!alwaysExpanded && performers.length > 0 && (
                <div
                  css={[
                    styles.expandMoreContainer,
                    css`
                      transition: transform 0.3s ease;
                      transform: ${isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
                    `,
                  ]}
                  onClick={toggleExpand}
                >
                  <ExpandMoreIcon />
                </div>
              )}


              {hasNextPage && (
                <div css={styles.showMoreContainer}>
                  <Button css={styles.showMoreButton} onClick={fetchMorePerformers}>
                    {isFetching ? 'Loading...' : 'Show More'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>


        <Dialog
          open={Boolean(modal)}
          onClose={close}
          maxWidth='xs'
          fullWidth
          css={styles.dialog}
          disableScrollLock
          fullScreen={isExtraSmallScreen}
          TransitionComponent={isExtraSmallScreen ? SlideTransition : FadeTransition}
        >
          {modal === 'invite' && (
            <InvitePerformers handleClose={close} performers={performers} performanceId={performanceId} />
          )}
          {modal === 'remove' && (
            <RemovePerformers handleClose={close} performers={performers} performanceId={performanceId} />
          )}
          {modal === 'permissions' && (
            <PerformancePermissions handleClose={close} performanceId={performanceId} />
          )}
        </Dialog>
      </div>
  )
}



const makeMapStateToProps = () => {
  const getPerformanceById = makeGetPerformanceById()
  const getPerformancePerformers = makeGetPerformancePerformers()

  const mapStateToProps = (state, props) => {
    
    const id = props.performanceId

    const performance = getPerformanceById(state, id).attributes
    
    const performers = getPerformancePerformers(state, id)

    const links = selectPerformanceLinks(state, id, 'performers')
    
    const role = get(state, ['user', 'attributes', 'role'])


    return {
      performers,
      stateLinks: links,
      isLoaded: selectPerformanceFetchedAssociations(state, id).includes('performers'),
      permission: ['admin', 'editor'].includes(role) ? 'owner' : performance?.permission,
    }
  }

  return mapStateToProps
}



const actions = {saveLinks, addPerformersToPerformance, setFetchedAssociations}

export default connect(makeMapStateToProps, actions)(EventLineup)




